import { trackRSCheckoutClicked } from '@app/analytics/track';
import { ShippingMethod } from '@app/types/cart/ShippingMethod';
import { CartQueryShippingAddress } from '@app/types/monolith/types';
import {
  trackGA4BeginCheckout,
  trackGA4SelectPaymentType,
} from '@app/utils/analytics';
import Box from '@redbubble/design-system/src/Box';
import Button from '@redbubble/design-system/src/Button';
import * as constants from '@redbubble/design-system/src/constants';
import { useViewportWidth } from '@redbubble/next/hooks';
import { defineMessages, useIntl } from '@redbubble/next/intl';
import logger from '@redbubble/next/logger';
import type { UserInfo } from '@redbubble/next/types';
import { useUserInfo } from '@redbubble/next-provider';
import {
  RSCheckoutLocation,
  type RSCheckoutLocationKey,
  useRudderstackClient,
} from '@redbubble/next-provider/rudderstack';
import {
  Elements,
  ExpressCheckoutElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import {
  ExpressCheckoutPartialAddress,
  ExpressPaymentType,
  loadStripe,
  Stripe,
  StripeElementLocale,
  StripeElementsOptions,
  StripeExpressCheckoutElementOptions,
  StripeExpressCheckoutElementReadyEvent,
} from '@stripe/stripe-js';
import React, { useRef, useState } from 'react';
import {
  handleClick,
  handleConfirm,
  handleShippingAddressChange,
  handleShippingRateChange,
} from './handlers';
import styles from './StripeExpress.module.scss';
import {
  ErrorMessage,
  LoadErrorEvent,
  PageSourceType,
  StripeAnalyticsItems,
  StripeCart,
  StripeExpressAnalyticsPageType,
} from './types';
import { usePay } from './usePay';
import { getStripePublishableKey, getSubunitAmount } from './utils';

export const STRIPE_EXPRESS_CHECKOUT_ELEMENT_ID =
  'stripe-express-checkout-element';

export const expressOptions: StripeExpressCheckoutElementOptions = {
  buttonHeight: 48,
  buttonType: {
    applePay: 'buy',
    googlePay: 'pay',
  },
  layout: {
    maxColumns: 1,
    maxRows: 0,
    overflow: 'never',
  },
  paymentMethods: {
    applePay: 'auto',
    googlePay: 'auto',
    link: 'never',
    paypal: 'never',
    amazonPay: 'never',
    // @ts-ignore: klarna is a payment option, the type is missing from Stripe ts package
    klarna: 'never',
  },
  paymentMethodOrder: ['applePay', 'googlePay'],
};

export const toElementsOptions = (
  cart: StripeCart,
  locale: StripeElementLocale
): StripeElementsOptions => {
  const { currency, total } = cart.summary;
  return {
    amount: getSubunitAmount(total),
    appearance: {
      variables: {
        borderRadius: '30px',
      },
    },
    captureMethod: 'manual',
    currency: currency.toLowerCase(),
    locale,
    mode: 'payment',
  };
};
interface ExpressCheckoutProps {
  analyticsPageType: StripeExpressAnalyticsPageType;
  rsCheckoutLocation: RSCheckoutLocationKey;
  pageSource: PageSourceType;
  cart: StripeCart;
  currency: string;
  onPayError: (message: ErrorMessage) => void;
  onLoadError: (event: LoadErrorEvent<'expressCheckout'>) => void;
  onReady: (event: StripeExpressCheckoutElementReadyEvent) => void;
  onLaunch: (paymentType: ExpressPaymentType) => void;
  onCancel: () => void;
  onConfirm: () => void;
  onShippingAddressChange: (
    shippingAddress: CartQueryShippingAddress
  ) => Promise<StripeCart>;
  onShippingPreferenceChange: (
    shippingMethod: ShippingMethod,
    shippingAddress: CartQueryShippingAddress
  ) => Promise<StripeCart>;
  rbEmail: string | null;
  consent?: boolean;
}

export const messages = defineMessages({
  buttonLoading: {
    defaultMessage: 'Express checkout button loading',
  },
});

const ExpressCheckout: React.FC<ExpressCheckoutProps> = ({
  analyticsPageType,
  pageSource,
  cart,
  currency,
  rbEmail,
  consent,
  onPayError,
  onLoadError,
  onReady,
  onLaunch,
  onCancel,
  onConfirm,
  onShippingAddressChange,
  onShippingPreferenceChange,
}) => {
  const elements = useElements();
  const stripe = useStripe();
  const intl = useIntl();
  const { isOrAbove } = useViewportWidth();
  const { post: postPay } = usePay();
  const shippingAddressRef = useRef<ExpressCheckoutPartialAddress>();

  return (
    stripe &&
    elements && (
      // The testId is needed here so that we can find the element in jest tests
      <Box data-testid={STRIPE_EXPRESS_CHECKOUT_ELEMENT_ID}>
        <ExpressCheckoutElement
          options={expressOptions}
          onClick={({ resolve, expressPaymentType }) => {
            // callback for the parent
            onLaunch(expressPaymentType);

            handleClick(resolve, elements, cart, intl, isOrAbove);
          }}
          onConfirm={async (event) => {
            onConfirm();

            await handleConfirm(
              analyticsPageType,
              pageSource,
              event,
              postPay,
              onPayError,
              currency,
              stripe,
              elements,
              rbEmail,
              consent
            );
          }}
          onShippingAddressChange={({ address, resolve, reject }) => {
            shippingAddressRef.current = address;
            handleShippingAddressChange(
              address,
              resolve,
              reject,
              elements,
              onShippingAddressChange,
              intl,
              isOrAbove
            );
          }}
          onShippingRateChange={({ shippingRate, resolve, reject }) =>
            handleShippingRateChange(
              shippingRate,
              onShippingPreferenceChange,
              elements,
              resolve,
              reject,
              intl,
              isOrAbove,
              shippingAddressRef.current
            )
          }
          onCancel={() => {
            shippingAddressRef.current = undefined;
            onCancel();
          }}
          onLoadError={onLoadError}
          onReady={onReady}
        />
      </Box>
    )
  );
};

interface ElementsProps {
  stripe: Promise<Stripe | null>;
  options: StripeElementsOptions;
}

export interface StripeExpressProps {
  analyticsPageType: StripeExpressAnalyticsPageType;
  rsCheckoutLocation: RSCheckoutLocationKey;
  pageSource: PageSourceType;
  cart: StripeCart;
  cartLoading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  onLaunch: () => void;
  onPayError: (message: ErrorMessage) => void;
  onShippingAddressChange: (
    shippingAddress: CartQueryShippingAddress
  ) => Promise<StripeCart>;
  onShippingPreferenceChange: (
    shippingMethod: ShippingMethod,
    shippingAddress?: CartQueryShippingAddress
  ) => Promise<StripeCart>;
  analyticsItems?: StripeAnalyticsItems;
  consent?: boolean;
}

export const StripeExpress = ({
  analyticsItems,
  analyticsPageType,
  rsCheckoutLocation,
  pageSource,
  cart,
  cartLoading,
  consent,
  onCancel,
  onConfirm,
  onLaunch,
  onPayError,
  onShippingAddressChange,
  onShippingPreferenceChange,
}: StripeExpressProps) => {
  const elementsPropsRef = useRef<ElementsProps | undefined>(undefined);
  const [stripeLoading, setStripeLoading] = useState(true);
  const [renderElement, setRenderElement] = useState(true);
  const [{ locale, isLoggedIn, email }] = useUserInfo<UserInfo>();
  const loading = cartLoading || stripeLoading;
  const intl = useIntl();
  const { RudderstackClient } = useRudderstackClient();

  if (!cart?.summary.total || !renderElement) return null;
  const { currency } = cart.summary;

  // Elements props cannot be changed after being set
  if (!elementsPropsRef.current) {
    const key = getStripePublishableKey({ currencyCode: currency });
    const stripePromise = loadStripe(key).catch(() => {
      setStripeLoading(false);
      setRenderElement(false);
      return null;
    });

    elementsPropsRef.current = {
      options: toElementsOptions(cart, locale as StripeElementLocale),
      stripe: stripePromise,
    };
  }

  const handleLoadError = () => {
    setStripeLoading(false);
    setRenderElement(false);
  };

  const handleReady = (event: StripeExpressCheckoutElementReadyEvent) => {
    setStripeLoading(false);

    const { availablePaymentMethods } = event;
    if (
      !availablePaymentMethods ||
      (!availablePaymentMethods.applePay && !availablePaymentMethods.googlePay)
    ) {
      setRenderElement(false);
      return;
    }
  };

  const handleLaunch = (paymentType: ExpressPaymentType) => {
    onLaunch();
    try {
      if (analyticsItems) {
        const cartTotal = cart?.summary.total;
        const couponCode = cart?.appliedCoupon?.code;
        trackGA4BeginCheckout({
          currency,
          items: analyticsItems,
          value: cartTotal,
          couponCode: couponCode,
        });
      }
      trackGA4SelectPaymentType({ paymentType, page: analyticsPageType });

      if (
        rsCheckoutLocation === RSCheckoutLocation.Cart ||
        rsCheckoutLocation === RSCheckoutLocation.CartTray
      ) {
        trackRSCheckoutClicked({
          rsClient: RudderstackClient,
          checkoutType: paymentType,
          location: rsCheckoutLocation,
        });
      }
    } catch (err) {
      const message = 'Unable to send begin_checkout tracking data to GA4';
      logger.error({ err, context: 'StripeExpress', message }, message);
    }
  };

  // NOTE: we wrap ExpressCheckout in a Box so we can hide the Stripe Element
  //       while loading and avoid the CSS eye bleeds that occur with margins

  return (
    <Elements
      stripe={elementsPropsRef.current.stripe}
      options={elementsPropsRef.current.options}
    >
      <Box className={`${loading ? styles.hidden : ''}`}>
        {renderElement && (
          <ExpressCheckout
            analyticsPageType={analyticsPageType}
            rsCheckoutLocation={rsCheckoutLocation}
            pageSource={pageSource}
            cart={cart}
            currency={currency}
            onCancel={onCancel}
            onShippingPreferenceChange={onShippingPreferenceChange}
            onShippingAddressChange={onShippingAddressChange}
            onPayError={onPayError}
            onLoadError={handleLoadError}
            onReady={handleReady}
            onLaunch={handleLaunch}
            onConfirm={onConfirm}
            rbEmail={isLoggedIn && email ? email : null}
            consent={consent}
          />
        )}
      </Box>
      {loading && (
        <Button
          type="button"
          size={constants.LARGE}
          loading
          fluid
          aria-label={intl.formatMessage(messages.buttonLoading)}
        >
          &nbsp;
        </Button>
      )}
    </Elements>
  );
};
